import update from 'immutability-helper';
import get from 'lodash/get';
import { orderBy } from 'natural-orderby';
import * as ACTIONS from '../actions';
import { BASE_COLUMNS } from '../constants';
import { getStageName } from '../helpers/getStageName';
import moment from 'moment';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import * as DRAWER_ACTIONS from '../components/Drawer/actions';

const orderByCreatedAt = (leads) => {
  if (!Array.isArray(leads)) return [];
  return orderBy(leads, [
    l => {
      const createdAt = get(l, 'created_at');
      return createdAt ? new Date(createdAt) : new Date(0);
    }, 
    l => get(l, 'id')
  ], ['desc', 'desc']);
};

const orderDesc = (leads) => {
  if (!Array.isArray(leads)) return [];
  return orderBy(leads, [l => get(l, 'tasks[0].due_at'), l => get(l, 'tasks[0].id')], ['asc', 'asc']);
}; 

const orderDescByColumnLog = (leads) => {
  if (!Array.isArray(leads)) return [];
  return orderBy(leads, [l => get(l, 'last_column_updated_at'), l => get(l, 'id')], ['desc', 'desc']);
};

// Utility function to order leads based on column type
const orderLeadsByColumn = (leads, columnId) => {
  if (columnId === 1) {
    // Leads column: sort by created_at (most recent first)
    return orderByCreatedAt(leads);
  } else if (columnId === 5 || columnId === 6) {
    // Follow Up / Negociação columns: sort by task due dates
    return orderDesc(leads);
  } else {
    // Other columns: sort by last_column_updated_at
    return orderDescByColumnLog(leads);
  }
};

// Utility function to merge columns safely
const mergeColumnWithData = (baseColumn, dataColumn, metaInfo = null) => {
  if (!baseColumn) return null;
  
  const leads = (dataColumn && dataColumn.leads) || [];
  
  return {
    ...baseColumn,
    leads: orderLeadsByColumn(leads, baseColumn.id),
    total: (dataColumn && dataColumn.total) || 0,
    selectedMonth: metaInfo && metaInfo.qtdd && metaInfo.qtdd[baseColumn.id] || 0
  };
};

const columnsReducer = (state = BASE_COLUMNS, action) => {
  // Ensure we always have a valid state
  const currentState = state || BASE_COLUMNS;
  
  switch (action.type) {
    case CACHE_ACTIONS.leads:
      try {
        const { columns: cachedStages, metaInfo, funnel, meta } = action.payload || {};
        
        // Log funnel data if available from cache
        if (funnel) {
        }
        
        // Handle cached stages data (new structure)
        if (Array.isArray(cachedStages) && cachedStages.length > 0 && cachedStages[0].hasOwnProperty('stage_id')) {
          const stagesConfig = (meta && meta.stages_config) || {};
          
          return currentState.map((column) => {
            const stageData = cachedStages.find(stage => stage.stage_id === column.id);
            if (stageData) {
              return {
                ...column,
                name: stageData.stage_name || column.name,
                leads: stageData.leads || [],
                total: stageData.total_count || 0,
                selectedMonth: stageData.count || 0,
                // Update style with cached colors if available
                style: stagesConfig[column.id] ? {
                  ...column.style,
                  color: stagesConfig[column.id].color
                } : column.style
              };
            }
            return {
              ...column,
              leads: [],
              total: 0,
              selectedMonth: 0
            };
          });
        }
        
        // Fallback: handle legacy cached data structure
        return currentState.map((column) => {
          const dataColumn = cachedStages && cachedStages.find(({ id }) => id === column.id);
          return mergeColumnWithData(column, dataColumn, metaInfo);
        }).filter(Boolean);
      } catch (error) {
        console.error('Error processing CACHE_ACTIONS.leads:', error);
        return currentState;
      }

    case ACTIONS.LOAD_LEADS.SUCCESS:
      try {
        // Handle API response structure using double data prop
        const responseData = action.payload && action.payload.data;
        const nestedData = responseData && responseData.data;
        const stages = nestedData && nestedData.stages || [];
        const stagesConfig = (action.payload && action.payload.meta && action.payload.meta.stages_config) || {};
        const funnelData = nestedData && nestedData.funnel || null;
        const summary = (funnelData && funnelData.summary) || null;
        
        if (stages.length > 0) {
          const updatedColumns = currentState.map((column) => {
            const stageData = stages.find(stage => stage.stage_id === column.id);
            if (stageData) {
              // Convert honorary field from formatted string to number for all leads
              const processedLeads = (stageData.leads || []).map(lead => {
                if (lead.honorary && typeof lead.honorary === 'string') {
                  const cleanValue = lead.honorary.replace('R$', '').trim();
                  const numericValue = parseFloat(cleanValue.replace(/\./g, '').replace(',', '.'));
                  return {
                    ...lead,
                    honorary: isNaN(numericValue) ? lead.honorary : numericValue
                  };
                }
                return lead;
              });
              
              return {
                ...column,
                name: stageData.stage_name || column.name, // Update name from API if available
                leads: processedLeads,
                total: stageData.total_count || 0,
                selectedMonth: stageData.count || 0,
                // Update style with new colors from backend if available
                style: stagesConfig[column.id] ? {
                  ...column.style,
                  color: stagesConfig[column.id].color,
                  name: stagesConfig[column.id].name || column.name
                } : column.style
              };
            }
            return {
              ...column,
              leads: [], // Empty leads for stages not returned by API
              total: 0,
              selectedMonth: 0
            };
          });
          
          return updatedColumns;
        }
        
        // Store funnel data if available (could be used by context or additional reducers)
        if (funnelData) {
        }
        
        // Return current state if no valid stages data found
        console.warn('No valid stages data found in API response');
        return currentState;
      } catch (error) {
        console.error('Error processing LOAD_LEADS.SUCCESS:', error);
        return currentState;
      }

    case ACTIONS.MOVE.INIT:
      try {
        const moveProps = action.props || [];
        
        // Extract data from the action props which contain the move details
        const sourceColumn = moveProps[2]; // source
        const destColumn = moveProps[3]; // destination  
        const leadId = parseInt(moveProps[4]); // draggableId
        
        const from = parseInt(sourceColumn.droppableId);
        const to = parseInt(destColumn.droppableId);
        
        // Find the lead being moved
        const sourceColumnData = currentState.find(col => col.id === from);
        const leadBeingMoved = sourceColumnData && sourceColumnData.leads 
          ? sourceColumnData.leads.find(lead => lead.id === leadId)
          : null;
        
        if (!leadBeingMoved || !from || !to) {
          console.error('Invalid MOVE.INIT data:', { leadBeingMoved, from, to, leadId });
          return currentState;
        }
        
        
        return currentState.map((column) => {
          if (column.id === from) {
            return update(column, {
              leads: { $set: (column.leads || []).filter(({ id }) => id !== leadId) }
            });
          }
          if (column.id === to) {
            const updatedLead = {
              ...leadBeingMoved,
              stage: to,
              stage_name: getStageName(to),
              last_column_updated_at: moment().format('YYYY-MM-DD HH:mm:ss')
            };
            
            // Place the moved lead at the top of the destination column
            const updatedLeads = [updatedLead, ...(column.leads || [])];

            return update(column, {
              leads: { $set: updatedLeads }
            });
          }
          return column;
        });
      } catch (error) {
        console.error('Error processing MOVE.INIT:', error);
        return currentState;
      }

    case ACTIONS.MOVE.SUCCESS:
      try {
        // Just return current state since optimistic update already happened
        return currentState;
      } catch (error) {
        console.error('Error processing MOVE.SUCCESS:', error);
        return currentState;
      }
      
    case ACTIONS.MOVE.FAIL:
      try {
        
        const moveProps = action.props || [];
        
        // Extract data from the action props which contain the move details
        const sourceColumn = moveProps[2]; // source
        const destColumn = moveProps[3]; // destination  
        const leadId = parseInt(moveProps[4]); // draggableId
        
        const from = parseInt(sourceColumn.droppableId);
        const to = parseInt(destColumn.droppableId);
        
        // Find the lead in the destination column (where it was moved to)
        const destColumnData = currentState.find(col => col.id === to);
        const leadToRevert = destColumnData && destColumnData.leads 
          ? destColumnData.leads.find(lead => lead.id === leadId)
          : null;
        
        if (!leadToRevert || !from || !to) {
          console.error('Invalid MOVE.FAIL data:', { leadToRevert, from, to, leadId });
          return currentState;
        }
        
        
        // Revert the move: remove from destination and add back to source
        return currentState.map((column) => {
          if (column.id === to) {
            return update(column, {
              leads: { $set: (column.leads || []).filter(({ id }) => id !== leadId) }
            });
          }
          if (column.id === from) {
            const revertedLead = {
              ...leadToRevert,
              stage: from,
              stage_name: getStageName(from),
              last_column_updated_at: leadToRevert.original_last_column_updated_at || leadToRevert.last_column_updated_at
            };
            
            // Apply proper ordering when reverting the move
            const updatedLeads = [...(column.leads || []), revertedLead];
            const orderedLeads = orderLeadsByColumn(updatedLeads, column.id);

            return update(column, {
              leads: { $set: orderedLeads }
            });
          }
          return column;
        });
      } catch (error) {
        console.error('Error processing MOVE.FAIL:', error);
        return currentState;
      }

    case ACTIONS.LOAD_SUMMARY.SUCCESS:
      try {
        // Handle lightweight summary endpoint response using double data prop
        const responseData = action.payload && action.payload.data;
        const nestedData = responseData && responseData.data;
        const funnelData = nestedData && nestedData.funnel || null;
        const summary = (funnelData && funnelData.summary) || {};
        const stagesConfig = (action.payload && action.payload.meta && action.payload.meta.stages_config) || {};
        
        // Update columns with summary data (counts only, no lead details)
        const updatedColumns = currentState.map((column) => {
          const stageCount = summary.by_stage && summary.by_stage[column.id] || 0;
          return {
            ...column,
            selectedMonth: stageCount,
            total: stageCount, // For summary, total equals current count
            // Update style with new colors from backend if available
            style: stagesConfig[column.id] ? {
              ...column.style,
              color: stagesConfig[column.id].color
            } : column.style
          };
        });
        
        // Store funnel data if available
        if (funnelData) {
        }
        
        return updatedColumns;
      } catch (error) {
        console.error('Error processing LOAD_SUMMARY.SUCCESS:', error);
        return currentState;
      }

    case ACTIONS.LOAD_MORE_LEADS.SUCCESS:
      try {
        const { columnId, data } = action.payload || {};
        
        if (!columnId || !Array.isArray(data)) {
          console.error('Invalid LOAD_MORE_LEADS.SUCCESS payload:', action.payload);
          return currentState;
        }
        
        return currentState.map((column) => {
          if (column.id === columnId) {
            const allLeads = [...(column.leads || []), ...data];
            const orderedLeads = orderLeadsByColumn(allLeads, column.id);

            return update(column, {
              leads: { $set: orderedLeads }
            });
          }
          return column;
        });
      } catch (error) {
        console.error('Error processing LOAD_MORE_LEADS.SUCCESS:', error);
        return currentState;
      }

    case ACTIONS.FETCHQTDD.SUCCESS:
      try {
        const qtddData = action.payload && action.payload.data && action.payload.data.qtddData;
        return currentState.map((column) => ({
          ...column,
          selectedMonth: (qtddData && qtddData[column.id]) || 0
        }));
      } catch (error) {
        console.error('Error processing FETCHQTDD.SUCCESS:', error);
        return currentState;
      }

    case DRAWER_ACTIONS.CREATE.SUCCESS:
      try {
        // Get the created lead data from the response
        const createdLead = action.payload && 
                           action.payload.data && 
                           action.payload.data.data && 
                           action.payload.data.data.lead;
        
        if (!createdLead || !createdLead.id || !createdLead.stage) {
          console.error('No lead data found in CREATE.SUCCESS action');
          return currentState;
        }

        // Convert honorary from formatted string to number if needed
        if (createdLead.honorary && typeof createdLead.honorary === 'string') {
          // Remove "R$" and convert formatted currency to number
          const cleanValue = createdLead.honorary.replace('R$', '').trim();
          // Convert Brazilian formatted number (1.000,00) to float
          const numericValue = parseFloat(cleanValue.replace(/\./g, '').replace(',', '.'));
          createdLead.honorary = isNaN(numericValue) ? createdLead.honorary : numericValue;
        }

        // Add the new lead to the appropriate column
        return currentState.map((column) => {
          if (column.id === createdLead.stage) {
            const updatedLeads = [...(column.leads || []), createdLead];
            const orderedLeads = orderLeadsByColumn(updatedLeads, column.id);
            
            return {
              ...column,
              leads: orderedLeads,
              total: column.total + 1,
              selectedMonth: column.selectedMonth + 1
            };
          }
          return column;
        });
      } catch (error) {
        console.error('Error processing CREATE.SUCCESS:', error);
        return currentState;
      }

    case DRAWER_ACTIONS.SAVE.SUCCESS:
      try {
        // Get the updated lead data from the response using double data prop structure
        const responseData = action.payload && action.payload.data;
        const nestedData = responseData && responseData.data;
        const updatedLead = nestedData && nestedData.lead;
        
        if (!updatedLead || !updatedLead.id) {
          console.error('No lead data found in SAVE.SUCCESS action');
          return currentState;
        }
        
        // Convert honorary from formatted string to number if needed
        if (updatedLead.honorary && typeof updatedLead.honorary === 'string') {
          // Remove "R$" and convert formatted currency to number
          const cleanValue = updatedLead.honorary.replace('R$', '').trim();
          // Convert Brazilian formatted number (1.000,00) to float
          const numericValue = parseFloat(cleanValue.replace(/\./g, '').replace(',', '.'));
          updatedLead.honorary = isNaN(numericValue) ? updatedLead.honorary : numericValue;
        }

        // Update the lead in the appropriate column
        return currentState.map((column) => {
          const leadIndex = (column.leads || []).findIndex(lead => lead.id === updatedLead.id);
          
          if (leadIndex >= 0) {
            // Lead exists in this column, update it in place
            const updatedLeads = [...column.leads];
            
            // Check if the stage has changed
            const oldLead = updatedLeads[leadIndex];
            const stageChanged = oldLead.stage !== updatedLead.stage;
            
            // Update the lead data while preserving position
            // Important: Merge all fields from the API response
            updatedLeads[leadIndex] = {
              ...updatedLeads[leadIndex],
              ...updatedLead,
              additional_data: {
                ...(updatedLeads[leadIndex].additional_data || {}),
                ...(updatedLead.additional_data || {}),
                nextPendingTask: null
              }
            };
            
            // Only re-order if the stage has changed or if critical sorting fields changed
            let finalLeads = updatedLeads;
            if (stageChanged) {
              // If stage changed, re-order the entire list
              finalLeads = orderLeadsByColumn(updatedLeads, column.id);
            }
            // For column 5 (Follow Up) and 6 (Negociação), re-order if task due date changed
            else if (column.id === 5 || column.id === 6) {
              const oldDueAt = oldLead.tasks && oldLead.tasks[0] && oldLead.tasks[0].due_at;
              const newDueAt = updatedLead.tasks && updatedLead.tasks[0] && updatedLead.tasks[0].due_at;
              if (oldDueAt !== newDueAt) {
                finalLeads = orderLeadsByColumn(updatedLeads, column.id);
              }
            }
            // For other columns, keep the lead in the same position unless explicitly moved
            
            return {
              ...column,
              leads: finalLeads
            };
          }
          return column;
        });
      } catch (error) {
        console.error('Error processing SAVE.SUCCESS:', error);
        return currentState;
      }

    case DRAWER_ACTIONS.DELETE.SUCCESS:
      try {
        // Get the deleted lead data from the axios middleware's stored location
        const deletedLead = (action.meta && 
                           action.meta.previousAction && 
                           action.meta.previousAction.payload && 
                           action.meta.previousAction.payload.lead) ||
                          (action.payload &&
                           action.payload.config &&
                           action.payload.config.reduxSourceAction &&
                           action.payload.config.reduxSourceAction.payload &&
                           action.payload.config.reduxSourceAction.payload.lead);
        
        if (!deletedLead || !deletedLead.id) {
          console.error('No lead data found in DELETE.SUCCESS action');
          return currentState;
        }

        // Remove the deleted lead from all columns
        return currentState.map((column) => ({
          ...column,
          leads: (column.leads || []).filter(lead => lead.id !== deletedLead.id),
          total: column.leads && column.leads.find(lead => lead.id === deletedLead.id) ? column.total - 1 : column.total,
          selectedMonth: column.leads && column.leads.find(lead => lead.id === deletedLead.id) ? column.selectedMonth - 1 : column.selectedMonth
        }));
      } catch (error) {
        console.error('Error processing DELETE.SUCCESS:', error);
        return currentState;
      }

    default:
      return currentState;
  }
};

export default columnsReducer;
